// AtriumNotificationBell.tsx — Atrium top-bar notifications bell + panel.
// ----------------------------------------------------------------------------
// Wires the previously-inert header bell to a real notifications UI. It REUSES
// the same data the NotificationCenter page uses (condoStore `notifications` /
// `fetchNotifications`, scoped to `currentCondo`) — no ad-hoc fetch logic, no
// new endpoint. Clicking the bell opens an AntD <Dropdown> panel (consistent
// with the avatar menu in AtriumTopBar) listing the most-recent notices with an
// unread (active) indicator; a <Badge> on the bell shows the active count, and a
// "View all" link drives shell navigation to the Communications hub (which hosts
// the full NotificationCenter under its Notifications tab).
import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, Badge, Empty, Spin, Typography, Button } from 'antd';
import {
  BellOutlined, InfoCircleOutlined, WarningOutlined,
  ExclamationCircleOutlined, ClockCircleOutlined, ToolOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCondoStore } from '../../store/condoStore';
import { fromNowServer } from '../../utils/relativeTime';
import type { OwnerNotification, NotificationCategory } from '../../types';

const { Text } = Typography;

/** Per-category glyph + accent (mirrors NotificationCenter's CATEGORY_CONFIG). */
const CATEGORY_CONFIG: Record<NotificationCategory, { color: string; icon: React.ReactNode }> = {
  INFO:        { color: 'var(--blue)',   icon: <InfoCircleOutlined /> },
  WARNING:     { color: '#d97706',       icon: <WarningOutlined /> },
  URGENT:      { color: 'var(--red)',    icon: <ExclamationCircleOutlined /> },
  REMINDER:    { color: '#7c3aed',       icon: <ClockCircleOutlined /> },
  MAINTENANCE: { color: '#0891b2',       icon: <ToolOutlined /> },
  FINANCIAL:   { color: '#16a34a',       icon: <DollarOutlined /> },
};

const MAX_ROWS = 8;

const iconBtnStyle: React.CSSProperties = {
  width: 38, height: 38, borderRadius: 10, background: 'var(--bg)',
  border: '1px solid var(--line)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', cursor: 'pointer', position: 'relative',
  color: 'var(--ink)', fontSize: 18,
};

const panelStyle: React.CSSProperties = {
  width: 360, maxHeight: 460, background: 'var(--card)',
  border: '1px solid var(--line)', borderRadius: 12,
  boxShadow: '0 12px 32px rgba(0,0,0,0.14)', overflow: 'hidden',
  display: 'flex', flexDirection: 'column',
};

const panelHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px', borderBottom: '1px solid var(--line)',
  fontWeight: 800, fontSize: 14, color: 'var(--ink)',
};

const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 11, padding: '11px 16px',
  borderBottom: '1px solid var(--line)', alignItems: 'flex-start',
};

export interface AtriumNotificationBellProps {
  /** Navigate to the full notifications surface (Communications hub). */
  onViewAll?: () => void;
}

export const AtriumNotificationBell: React.FC<AtriumNotificationBellProps> = ({ onViewAll }) => {
  const {
    notifications, fetchNotifications, currentCondo, isLoading,
  } = useCondoStore() as any;

  const [open, setOpen] = useState(false);

  // Same fetch-on-mount / on-condo-change contract NotificationCenter uses:
  // pull the live (active) notices for the active condo. Reuses the existing
  // store action only — condo-scoped via currentCondo.id.
  useEffect(() => {
    if (currentCondo?.id) fetchNotifications(currentCondo.id, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchNotifications is a stable zustand action
  }, [currentCondo?.id]);

  // Wrap in useMemo so the `?? []` fallback doesn't produce a fresh array
  // identity on every render (which would churn the unreadCount/recent memos).
  const list: OwnerNotification[] = useMemo(() => notifications ?? [], [notifications]);

  // "Unread" maps to the active flag — the same signal NotificationCenter and
  // CommunicationsHub treat as a live/unread notice.
  const unreadCount = useMemo(
    () => list.filter(n => n.active).length,
    [list],
  );

  const recent = useMemo(
    () => [...list]
      .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
      .slice(0, MAX_ROWS),
    [list],
  );

  const handleViewAll = () => {
    setOpen(false);
    onViewAll?.();
  };

  const panel = (
    <div style={panelStyle} role="dialog" aria-label="Notifications">
      <div style={panelHeaderStyle}>
        <span>Notifications</span>
        {unreadCount > 0 && (
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
            {unreadCount} active
          </Text>
        )}
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {isLoading && list.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center' }}><Spin /></div>
        ) : recent.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
            style={{ padding: '28px 0' }}
          />
        ) : (
          recent.map(n => {
            const cfg = CATEGORY_CONFIG[n.category] || CATEGORY_CONFIG.INFO;
            return (
              <div key={n.id} style={rowStyle}>
                <span style={{ color: cfg.color, fontSize: 16, marginTop: 1 }} aria-hidden="true">
                  {cfg.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Text strong style={{ fontSize: 13, color: 'var(--ink)' }} ellipsis>
                      {n.title}
                    </Text>
                    {n.active && (
                      <span aria-label="unread" style={{
                        flex: 'none', width: 7, height: 7, borderRadius: '50%',
                        background: 'var(--red)',
                      }} />
                    )}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }} ellipsis>
                    {n.message}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {fromNowServer(n.createdAt)}
                  </Text>
                </div>
              </div>
            );
          })
        )}
      </div>

      {onViewAll && (
        <div style={{ padding: 10, borderTop: '1px solid var(--line)', textAlign: 'center' }}>
          <Button type="link" size="small" onClick={handleViewAll}>
            View all notifications
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomRight"
      popupRender={() => panel}
    >
      <button
        type="button"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        title="Notifications"
        style={iconBtnStyle}
      >
        <Badge count={unreadCount} size="small" offset={[2, -2]}>
          <BellOutlined aria-hidden="true" style={{ color: 'var(--ink)', fontSize: 18 }} />
        </Badge>
      </button>
    </Dropdown>
  );
};
